import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import api, { MaintenanceAPI } from "../../api";
import "./sidebar.css";
import { resolveMediaUrl } from "../../utils/url";

export default function Sidebar() {
  const { user, ready } = useContext(UserContext);
  const [maint, setMaint] = useState(null);
  const [menuConfig, setMenuConfig] = useState([]);
  const [serviceNames, setServiceNames] = useState([]);
  const [liveCount, setLiveCount] = useState(0);
  const [liveEntries, setLiveEntries] = useState([]);
  const [showLivePanel, setShowLivePanel] = useState(false);
  const liveAnchorRef = useRef(null);
  const [livePanelPos, setLivePanelPos] = useState({ top: 0, left: 0 });
  const hasLoggedErrorRef = useRef(false);
  // Items are now driven entirely by backend sidebar-config

  const DEFAULT_MENU = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: '🏠',
      path: '/dashboard',
      roles: ['member','mentor','moderator','admin','owner'],
      order: 50,
      type: 'item',
      group: ''
    },
    {
      key: 'supportplan',
      label: 'Supportplan',
      icon: '📅',
      path: '/supportplan',
      roles: ['member','mentor','moderator','admin','owner'],
      order: 120,
      type: 'item',
      group: ''
    }
  ];

  // Live-Count: Anzahl aktuell laufender Streams aus dem Supportplan
  useEffect(() => {
    let mounted = true;
    const fetchLive = async () => {
      try {
        const nowIso = new Date().toISOString();
        const res = await api.get('/supportplan/entries', { params: { weekStart: nowIso, weekEnd: nowIso } });
        if (!mounted) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const count = list.length;
        setLiveCount(count);
        setLiveEntries(list);
      } catch (e) {
        if (mounted) { setLiveCount(0); setLiveEntries([]); }
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, 30000); // alle 30 Sekunden aktualisieren
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // Maintenance status (fetch and refresh periodically)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const s = await MaintenanceAPI.status();
        if (!mounted) return;
        setMaint(s || null);
      } catch {
        if (mounted) setMaint(null);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // Load my service subscriptions once to gate certain items (e.g., Impressum)
  useEffect(() => {
    let mounted = true;
    if (!user) return;
    api.get('/subscriptions/me')
      .then(res => {
        if (!mounted) return;
        const names = Array.isArray(res.data?.items) ? res.data.items.map(it => it.Service?.name).filter(Boolean) : [];
        // de-duplicate
        const uniq = Array.from(new Set(names));
        setServiceNames(uniq);
      })
      .catch(() => { if (mounted) setServiceNames([]); });
    return () => { mounted = false; };
  }, [user?.id]);

  const resolvePathTemplate = (path) => {
    const raw = (typeof path === 'string' ? path : String(path ?? '')).trim();
    if (!raw) return '';

    const username = (user && user.username) ? String(user.username).trim() : '';
    const needsUsername = raw.includes('{{username}}') || raw.includes('{username}') || raw.includes(':username');
    if (needsUsername) {
      if (!username) return '';
      const encoded = encodeURIComponent(username);
      // Use split/join for wide browser support (avoid replaceAll assumptions)
      return raw
        .split('{{username}}').join(encoded)
        .split('{username}').join(encoded)
        .split(':username').join(encoded);
    }

    return raw;
  };

  const maintenanceOn = !!(maint && maint.enabled);

  if (!ready || !user) return (
    <aside className={`apn-sidebar${maintenanceOn ? ' maintenance-on' : ''}`} aria-label="Mitglieder-Navigation">
      <div className="apn-sidebar-inner" style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
        <div className="sidebar-loader" style={{padding:32,opacity:.7}}>
          <span style={{fontSize:28}}>⏳</span>
        </div>
      </div>
    </aside>
  );

  // Map: driven by backend
  const itemsFromBackend = Array.isArray(menuConfig) ? menuConfig.filter(e => e.type !== 'group') : [];
  const groupsFromBackend = Array.isArray(menuConfig) ? menuConfig.filter(e => e.type === 'group') : [];
  const groupIconMap = {};
  for (const g of groupsFromBackend) {
    groupIconMap[g.key] = g.icon;
  }

  // State für ausgeklappte Gruppen
  const [expandedGroups, setExpandedGroups] = useState({});

  // Standard: alle Gruppen ausgeklappt beim ersten Render + Fallback, logge Fehler nur einmal
  useEffect(() => {
    let mounted = true;
    api.get("/sidebar-config")
      .then(res => {
        if (!mounted) return;
        setMenuConfig(res.data);
        const initialGroups = {};
        for (const entry of res.data) {
          if (entry.type === "group") initialGroups[entry.key] = true;
        }
        setExpandedGroups(initialGroups);
      })
      .catch(err => {
        if (!mounted) return;
        if (!hasLoggedErrorRef.current) {
          console.error('Sidebar: Laden der Konfiguration fehlgeschlagen', err?.response?.status || err?.message || err);
          hasLoggedErrorRef.current = true;
        }
        // Fallback auf minimales Menü für eingeloggte Nutzer
        setMenuConfig(DEFAULT_MENU);
        setExpandedGroups({});
      });
    return () => { mounted = false; };
  }, []);

  // Handler für Ein-/Ausklappen
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <aside className={`apn-sidebar${maintenanceOn ? ' maintenance-on' : ''}`} aria-label="Mitglieder-Navigation">
      <div className="apn-sidebar-inner">
        {/* Fester Bereich ganz oben: Platz für zusätzliche Links ohne Layout-Shift */}
        <div
          className="apn-sidebar-top"
          style={{
            height: 84, // Fixe Höhe verhindert Verschiebungen der Menüpunkte
            padding: '10px 10px 8px',
            marginBottom: 6,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.06)',
            border: '1px solid #222',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            overflow: 'visible',
            position: 'relative',
          }}
          aria-label="Schnellzugriffe"
        >
          <div
            onMouseEnter={() => setShowLivePanel(true)}
            onMouseLeave={() => {
              // Small delay to allow moving the cursor into the panel without flicker
              setTimeout(() => setShowLivePanel(false), 120);
            }}
            style={{ position: 'relative', paddingBottom: 8 }}
          >
            {(() => {
              const active = liveCount > 0;
              const badgeBg = active ? 'rgba(231,76,60,0.15)' : 'rgba(255,255,255,0.12)';
              const badgeColor = active ? '#e74c3c' : '#fff';
              const dotColor = active ? '#e74c3c' : '#fff';
              const dotOpacity = active ? 1 : 0.7;
              return (
                <span style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background: badgeBg, color: badgeColor,
                  padding:'4px 10px', borderRadius: 16, fontWeight: 700, fontSize: 13,
                  cursor: active ? 'pointer' : 'default'
                }} title="Aktuell laut Streamplan" ref={liveAnchorRef}
               onMouseEnter={() => {
                 // compute panel position near the badge
                 const el = liveAnchorRef.current;
                 if (el) {
                   const rect = el.getBoundingClientRect();
                   const gap = 6;
                   const estimatedWidth = 320; // match panel maxWidth
                   const left = Math.min(
                     Math.max(8, rect.left),
                     Math.max(8, (window.innerWidth || 0) - estimatedWidth - 8)
                   );
                   const top = rect.bottom + gap;
                   setLivePanelPos({ top, left });
                 }
                 setShowLivePanel(true);
               }}
            >
              <span style={{width:8,height:8,borderRadius:'50%',background:dotColor,opacity:dotOpacity,display:'inline-block'}} />
              Aktuelle Livestreams: {liveCount}
            </span>
              );
            })()}
            {showLivePanel && createPortal(
              <div
                onMouseEnter={() => setShowLivePanel(true)}
                onMouseLeave={() => setShowLivePanel(false)}
                style={{
                  position: 'fixed',
                  top: livePanelPos.top,
                  left: livePanelPos.left,
                  minWidth: 260,
                  maxWidth: 320,
                  maxHeight: 260,
                  overflow: 'auto',
                  background: '#111',
                  color: '#eee',
                  border: '1px solid #333',
                  borderRadius: 10,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                  padding: 10,
                  zIndex: 10000,
                  pointerEvents: 'auto',
                }}
              >
                <div style={{fontWeight:700, fontSize:14, marginBottom:6}}>Jetzt live</div>
                {liveEntries.length === 0 ? (
                  <div style={{opacity:.7, fontSize:13}}>Niemand ist gerade live.</div>
                ) : (
                  <ul style={{listStyle:'none', margin:0, padding:0, display:'grid', gap:8}}>
                    {liveEntries.map(e => {
                      const url = (e.streamLink && (e.streamLink.startsWith('http://') || e.streamLink.startsWith('https://')))
                        ? e.streamLink
                        : (e.streamLink ? `https://${e.streamLink}` : null);
                      const platform = e.platform || '';
                      const title = e.title || '';
                      const display = e.userDisplayName || e.userId;
                      return (
                        <li key={e.id} style={{display:'flex', alignItems:'center', gap:8}}>
                          <img src={resolveMediaUrl(e.avatarUrl || '/avatars/default-avatar.png')} alt="avatar" style={{width:20, height:20, borderRadius:'50%', objectFit:'cover', border:'1px solid #333'}} />
                          <div style={{display:'flex', flexDirection:'column', minWidth:0}}>
                            <div style={{fontWeight:600, fontSize:13, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden'}}>{display}</div>
                            <div style={{fontSize:11, opacity:.8, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden'}}>{platform}{title ? ` · ${title}` : ''}</div>
                          </div>
                          <div style={{marginLeft:'auto'}}>
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  background:'#e74c3c', color:'#fff', padding:'4px 8px', borderRadius:6,
                                  textDecoration:'none', fontSize:12, fontWeight:700
                                }}
                                title="Stream öffnen"
                              >Öffnen</a>
                            ) : (
                              <span style={{fontSize:12, opacity:.5}}>kein Link</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>, document.body)
            }
          </div>
        </div>
        <div className="apn-sidebar-head">
          <div className="apn-sidebar-title">Mitglieder-Menü</div>
        </div>
        {/* Gruppierte Sidebar-Menüpunkte */}
        {(() => {
          let rawItems = itemsFromBackend
            .filter(e => Array.isArray(user.roles) && Array.isArray(e.roles) && e.roles.some(r => user.roles.includes(r)))
            .map(e => {
              const raw = typeof e.path === 'string' ? e.path.trim() : '';
              const templated = raw ? (raw.startsWith('/') ? raw : `/${raw}`) : '';
              const to = resolvePathTemplate(templated);
              return {
                key: e.key,
                to,
                order: typeof e.order === 'number' ? e.order : 100,
                group: e.group || 'Sonstiges',
                label: e.label,
                icon: e.icon
              };
            })
            .sort((a, b) => a.order - b.order);
          // Conditional visibility for Impressum: only show if admin/owner or service assigned
          const hasAdmin = Array.isArray(user.roles) && (user.roles.includes('admin') || user.roles.includes('owner'));
          if (!hasAdmin) {
            const impressumIdx = rawItems.findIndex(i => i.key === 'impressum-service');
            if (impressumIdx !== -1) {
              // Basic heuristic: only show if user.services contains any matching names
              const svcNames = Array.isArray(serviceNames) ? serviceNames : [];
              const allowedNames = ['Impressum Lite','Impressum Full','Impressum Extra','Impressum','Impressums-Service Lite','Impressums-Service Full','Impressums-Service Extra'];
              const ok = svcNames.some(n => allowedNames.includes(n));
              if (!ok) rawItems.splice(impressumIdx, 1);
            }
          }
          // Optional: Duplikate anhand des stabilen Keys entfernen
          const seen = new Set();
          const visibleItems = rawItems.filter(it => {
            const k = it.key || it.to;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
          // Gruppiere nach group
          const grouped = {};
          for (const item of visibleItems) {
            const group = item.group || 'Sonstiges';
            if (!grouped[group]) grouped[group] = [];
            grouped[group].push(item);
          }
          // Render
          return Object.entries(grouped).map(([group, items], idx) => {
            const isExpanded = expandedGroups[group] !== false; // default: true
            return (
              <div key={group} className="sidebar-group" style={{marginBottom:20}}>
                <div className="sidebar-group-title" style={{
                  fontWeight:'bold',
                  fontSize:17,
                  display:'flex',
                  alignItems:'center',
                  background:'rgba(0,0,0,0.04)',
                  borderRadius:6,
                  padding:'8px 12px',
                  marginBottom:5,
                  marginTop: idx === 0 ? 0 : 12,
                  boxShadow:'0 1px 4px rgba(0,0,0,0.04)'
                }}>
                  {groupIconMap[group] ? <span style={{fontSize:22,marginRight:8}}>{groupIconMap[group]}</span> : null}
                  <span
                    onClick={() => toggleGroup(group)}
                    style={{
                      cursor:'pointer',
                      userSelect:'none',
                      marginRight:8,
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={isExpanded ? `Gruppe ${group} einklappen` : `Gruppe ${group} ausklappen`}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleGroup(group)}
                  >
                    {group}
                  </span>
                  <button
                    onClick={() => toggleGroup(group)}
                    style={{
                      marginLeft:'auto',
                      background:'none',
                      border:'none',
                      cursor:'pointer',
                      fontSize:22,
                      fontWeight:'bold',
                      padding:'0 4px',
                      color:'#fff',
                      textShadow:'0 0 2px #888, 0 0 6px #222',
                    }}
                    aria-label={isExpanded ? `Gruppe ${group} einklappen` : `Gruppe ${group} ausklappen`}
                  >
                    {isExpanded ? <span style={{fontWeight:'bold',color:'#fff',textShadow:'0 0 2px #888, 0 0 6px #222'}}>−</span> : <span style={{fontWeight:'bold',color:'#fff',textShadow:'0 0 2px #888, 0 0 6px #222'}}>+</span>}
                  </button>
                </div>
                {isExpanded && (
                  <ul className="apn-sidebar-nav" style={{margin:0,padding:0}}>
                    {items.map(i => {
                      const hasLink = !!i.to;
                      return (
                        <li key={i.key || `${i.group}:${i.to}`} style={{marginBottom:0.5}}>
                          {hasLink ? (
                            <NavLink to={i.to} className={({isActive}) => isActive ? "active" : ""} style={{padding:'2px 12px',borderRadius:4,fontSize:15}}>
                              {i.icon ? i.icon + " " : ""}
                              {i.label || i.key}
                            </NavLink>
                          ) : (
                            <span style={{padding:'2px 12px',borderRadius:4,fontSize:15,opacity:.6,cursor:'not-allowed',display:'inline-flex',alignItems:'center',gap:6}} title="Kein Pfad konfiguriert">
                              {i.icon ? i.icon + " " : ""}
                              {i.label || i.key}
                              {/* Hinweis für Admins */}
                              {Array.isArray(user.roles) && user.roles.includes('admin') ? <span aria-hidden="true" title="In der Sidebar-Konfiguration Pfad setzen">⚠</span> : null}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          });
        })()}
      </div>
    </aside>
  );
}
